import React from 'react';
import { MathItem } from '../../types/math';
import { MathFormula } from './MathFormula';
import { Lightbulb, Compass, Layers, CheckCircle2, XCircle } from 'lucide-react';

interface IntuitionDrawerProps {
  item: MathItem;
  selectedOptionId: string | null;
  isCorrect: boolean | null;
}

export const IntuitionDrawer: React.FC<IntuitionDrawerProps> = ({
  item,
  selectedOptionId,
  isCorrect
}) => {
  return (
    <div className="w-full bg-slate-900/90 border border-slate-800 rounded-xl p-5 shadow-2xl backdrop-blur-md transition-all duration-300">
      {/* Header Status */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
        <div className="flex items-center gap-2">
          {isCorrect === true && (
            <div className="flex items-center gap-1.5 text-teal-400 font-semibold text-sm">
              <CheckCircle2 className="w-5 h-5 text-teal-400" /> Correct! Mathematical Intuition Confirmed
            </div>
          )}
          {isCorrect === false && (
            <div className="flex items-center gap-1.5 text-rose-400 font-semibold text-sm">
              <XCircle className="w-5 h-5 text-rose-400" /> Common Trap! Here is the Visual Diagnostic:
            </div>
          )}
          {isCorrect === null && (
            <div className="flex items-center gap-1.5 text-amber-400 font-semibold text-sm">
              <Lightbulb className="w-5 h-5 text-amber-400" /> Visual Diagnostic Clues
            </div>
          )}
        </div>
        <span className="text-xs font-mono px-2.5 py-1 rounded bg-slate-800 text-slate-300 border border-slate-700">
          {item.dimension.toUpperCase()} · {item.title}
        </span>
      </div>

      {/* Governing Equation */}
      <div className="mb-4 bg-slate-950/70 p-3.5 rounded-lg border border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div>
          <span className="text-xs text-slate-400 uppercase tracking-wider font-mono block mb-1">
            Governing Equation:
          </span>
          <div className="text-lg text-teal-300 font-medium">
            <MathFormula latex={item.formulaLatex} displayMode={false} />
          </div>
        </div>
        <div className="text-sm text-slate-300 max-w-md text-left sm:text-right">
          {item.intuition.summary}
        </div>
      </div>

      {/* Key Signatures */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-slate-950/40 p-3.5 rounded-lg border border-slate-800/50">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            <Compass className="w-3.5 h-3.5 text-sky-400" /> Structural Signatures
          </div>
          <ul className="space-y-1.5 text-sm text-slate-300">
            {item.intuition.keySignatures.map((sig, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-sky-400 text-xs mt-1">▸</span>
                <span>{sig}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* 2D Dominant behavior or 3D Cross-sections */}
        <div className="bg-slate-950/40 p-3.5 rounded-lg border border-slate-800/50">
          {item.dimension === '2d' ? (
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                <Layers className="w-3.5 h-3.5 text-purple-400" /> Asymptotic & End Behavior
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">
                {item.intuition.dominantBehavior}
              </p>
              {item.asymptotes?.vertical && item.asymptotes.vertical.length > 0 && (
                <div className="mt-2 text-xs font-mono text-rose-300">
                  Vertical Asymptotes at x = {item.asymptotes.vertical.join(', ')}
                </div>
              )}
              {item.asymptotes?.horizontal && item.asymptotes.horizontal.length > 0 && (
                <div className="mt-1 text-xs font-mono text-amber-300">
                  Horizontal Asymptotes at y = {item.asymptotes.horizontal.join(', ')}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                <Layers className="w-3.5 h-3.5 text-purple-400" /> Cross-Sectional 2D Slices
              </div>
              <div className="space-y-2">
                {item.intuition.crossSections.map((cs, idx) => (
                  <div key={idx} className="text-xs bg-slate-900/60 p-2 rounded border border-slate-800">
                    <span className="font-mono text-purple-300 font-semibold">{cs.plane}</span>:{' '}
                    <span className="text-slate-300">{cs.description}</span>
                    <div className="mt-1 font-mono text-teal-300">
                      <MathFormula latex={cs.formulaLatex} displayMode={false} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Distractor explanations if user got it wrong */}
      {selectedOptionId && !isCorrect && (
        <div className="bg-rose-950/20 border border-rose-900/40 rounded-lg p-3 text-xs text-rose-200">
          <span className="font-semibold text-rose-300">Why the selected answer differed:</span>
          {item.distractors.map((d, i) => (
            <p key={i} className="mt-1">
              • <MathFormula latex={d.formulaLatex} displayMode={false} className="mr-1" />: {d.explanation}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
